import React, { useCallback, useEffect, useState } from 'react';

import { Badge, Group, Text, useMantineTheme } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { IconCopy, IconEdit, IconEye, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import useCreateWorkoutBenchmark from 'hooks/workoutBenchmark/useCreateWorkoutBenchmark';
import useWorkoutBenchmarks from 'hooks/workoutBenchmark/useWorkoutBenchmarks';
import { CellProps } from 'react-table';
import { WorkoutMovementList, WorkoutMovementWrite } from 'types';
import { MovementTypeEnum, SCORE_TYPE_LABEL_MAP, ScoreTypeEnum } from 'types/enums';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import ModalBenchmarkDelete from './ModalBenchmarkDelete';
import ModalBenchmarkForm from './ModalBenchmarkForm';
import ModalBenchmarkView from './ModalBenchmarkView';

interface IBenchmarksTableContainerProps {
  permission?: boolean;
  search?: string;
  source?: string;
}

const BenchmarksTableContainer = ({
  permission = true,
  search,
  source,
}: IBenchmarksTableContainerProps): JSX.Element => {
  const theme = useMantineTheme();
  const [ordering, setOrdering] = useState('name');
  const [ascending, setAscending] = useState(true);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useWorkoutBenchmarks({
      ordering: ascending ? ordering : `-${ordering}`,
      search,
      source,
    });

  const [selected, setSelected] = useState<WorkoutMovementList | undefined>();
  const [tableData, setTableData] = useState<WorkoutMovementList[]>([]);

  const [isViewOpened, { open: openView, close: closeView }] = useDisclosure(false);
  const [isEditFormOpened, { open: openEditForm, close: closeEditForm }] = useDisclosure(false);
  const [debouncedIsEditFormOpened] = useDebouncedValue(isEditFormOpened, 0);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const createMutation = useCreateWorkoutBenchmark();
  const { successNotification, errorNotification } = notificationHandler();

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setOrdering(header);
      setAscending(!ascending);
    },
    [ascending],
  );

  const handleView = (benchmark: WorkoutMovementList): void => {
    setSelected(benchmark);
    openView();
  };

  const handleEdit = (benchmark: WorkoutMovementList): void => {
    setSelected(benchmark);
    openEditForm();
  };

  const handleCloseEdit = (): void => {
    setSelected(undefined);
    closeEditForm();
  };

  const handleDelete = (benchmark: WorkoutMovementList): void => {
    setSelected(benchmark);
    openDeleteModal();
  };

  const handleDuplicate = (benchmark: WorkoutMovementList): void => {
    const payload: WorkoutMovementWrite = {
      workoutMovementId: uuidv4(),
      name: `${benchmark.name} (copy)`,
      description: benchmark.description,
      defaultScoreType: benchmark.defaultScoreType,
      movementType: MovementTypeEnum.BENCHMARK,
    };

    createMutation.mutate(payload, {
      onSuccess: () => {
        successNotification('Benchmark duplicated', `${payload.name} has been created successfully.`);
      },
      onError: error => {
        const errorMessage = error.response?.data?.message || 'Failed to duplicate benchmark';
        errorNotification('Duplication failed', errorMessage);
      },
    });
  };

  const columns = [
    {
      Header: 'Benchmark Name',
      accessor: 'name' as const,
      Cell: ({ row }: CellProps<WorkoutMovementList>) => (
        <Text
          onClick={() => handleView(row.original)}
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
        >
          {row.original.name}
        </Text>
      ),
    },
    {
      Header: 'Source',
      accessor: 'source' as const,
      Cell: ({ row }: CellProps<WorkoutMovementList>) => {
        const isDefault = row.original.source === 'Default';
        return (
          <Badge color={isDefault ? 'gray' : 'blue'} variant='filled'>
            {row.original.source}
          </Badge>
        );
      },
    },
    {
      Header: 'Scoring Type',
      accessor: 'defaultScoreType' as const,
      Cell: ({ row }: CellProps<WorkoutMovementList>) => {
        const value = row.original.defaultScoreType;
        if (!value) {
          return (
            <Text color='dimmed' size='sm'>
              —
            </Text>
          );
        }
        return <Text size='sm'>{SCORE_TYPE_LABEL_MAP[value as ScoreTypeEnum]}</Text>;
      },
    },
    {
      Header: 'Actions',
      id: 'action',
      Cell: ({ row }: CellProps<WorkoutMovementList>) => {
        // Default benchmarks are system-seeded — Delete stays visible but disabled.
        const isDefault = row.original.source === 'Default';
        return (
          <Group position='right' px='xs'>
            <Menu
              position='bottom-end'
              disabled={!permission}
              items={[
                {
                  title: 'View',
                  onClick: () => handleView(row.original),
                  icon: <IconEye size={theme.fontSizes.sm} />,
                },
                {
                  title: 'Edit',
                  onClick: () => handleEdit(row.original),
                  icon: <IconEdit size={theme.fontSizes.sm} />,
                },
                {
                  title: 'Duplicate',
                  onClick: () => handleDuplicate(row.original),
                  icon: <IconCopy size={theme.fontSizes.sm} />,
                },
                {
                  title: 'Delete',
                  onClick: isDefault ? undefined : () => handleDelete(row.original),
                  icon: <IconTrashX size={theme.fontSizes.sm} />,
                  color: isDefault ? undefined : 'red',
                  disabled: isDefault,
                  rightSection: isDefault ? (
                    <Text size='xs' color='dimmed' ml='xs'>
                      (Cannot delete default)
                    </Text>
                  ) : undefined,
                },
              ]}
            />
          </Group>
        );
      },
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['name']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
        margin={12}
        bgc='white'
      />

      <ModalBenchmarkView
        opened={isViewOpened}
        closeHandler={closeView}
        benchmark={selected}
        onEdit={() => {
          closeView();
          handleEdit(selected!);
        }}
        permission={permission}
      />

      <ModalBenchmarkForm
        opened={debouncedIsEditFormOpened}
        closeHandler={handleCloseEdit}
        benchmarkId={selected?.workoutMovementId}
        type='edit'
        permission={permission}
      />

      {selected && (
        <ModalBenchmarkDelete
          opened={!!selected && deleteModalOpened}
          handleClose={closeDeleteModal}
          benchmark={selected}
        />
      )}
    </>
  );
};

export default BenchmarksTableContainer;
